import { supabase } from '../../config/supabaseClient';
import { PRODUCT_IMAGES_BUCKET } from '../../config/supabase';
import { SITE } from '../../config/site';
import { escapeHtml, isProbablyAdminFromStorage, onReady, sanitizeFileName } from '../../utils/dom';

type GalleryItem = {
  url: string;
  path?: string;
  bucket?: string;
  title?: string;
  created_at?: string;
};

const GALLERY_KEY = 'company_gallery';
const GALLERY_FOLDER = 'company-gallery';
const MAX_FILES = 12;
const MAX_FILE_SIZE = 5 * 1024 * 1024;

function normalizeGalleryValue(value: unknown): GalleryItem[] {
  if (!value) return [];
  if (Array.isArray(value)) return value as GalleryItem[];

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? (parsed as GalleryItem[]) : [];
    } catch {
      return [];
    }
  }

  return [];
}

async function loadGallery(): Promise<GalleryItem[]> {
  const { data, error } = await supabase
    .from('site_info')
    .select('value')
    .eq('key', GALLERY_KEY)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return [];
    console.error('Errore caricamento gallery:', error);
    return [];
  }

  return normalizeGalleryValue(data?.value);
}

async function saveGallery(items: GalleryItem[]): Promise<void> {
  const { error } = await supabase.from('site_info').upsert(
    {
      key: GALLERY_KEY,
      value: items,
      updated_at: new Date().toISOString()
    },
    { onConflict: 'key' }
  );

  if (error) throw error;
}

async function uploadGalleryFiles(files: FileList | null): Promise<void> {
  if (!files) return;

  const bucket = PRODUCT_IMAGES_BUCKET;
  const current = await loadGallery();
  const remainingSlots = Math.max(0, MAX_FILES - current.length);
  const selectedFiles = Array.from(files).slice(0, remainingSlots);

  if (!selectedFiles.length) {
    alert('Hai già raggiunto il limite massimo di immagini.');
    return;
  }

  const uploadedItems: GalleryItem[] = [];

  for (const file of selectedFiles) {
    if (!file.type.startsWith('image/')) {
      alert(`File non valido: ${file.name}`);
      continue;
    }

    if (file.size > MAX_FILE_SIZE) {
      alert(`Immagine troppo grande: ${file.name}. Massimo 5MB.`);
      continue;
    }

    const safeName = sanitizeFileName(file.name);
    const path = `${GALLERY_FOLDER}/${Date.now()}-${Math.random().toString(36).slice(2)}-${safeName}`;

    const { error: uploadError } = await supabase.storage.from(bucket).upload(path, file, {
      cacheControl: '3600',
      upsert: false
    });

    if (uploadError) {
      console.error('Errore upload immagine:', uploadError);
      alert(`Errore durante il caricamento di ${file.name}.`);
      continue;
    }

    const { data: publicData } = supabase.storage.from(bucket).getPublicUrl(path);

    uploadedItems.push({
      url: publicData.publicUrl,
      path,
      bucket,
      title: file.name,
      created_at: new Date().toISOString()
    });
  }

  if (!uploadedItems.length) return;

  const nextGallery = [...current, ...uploadedItems].slice(0, MAX_FILES);
  await saveGallery(nextGallery);
}

async function removeGalleryImage(index: number): Promise<void> {
  const current = await loadGallery();
  const item = current[index];
  if (!item) return;

  const nextGallery = current.filter((_, itemIndex) => itemIndex !== index);

  if (item.bucket && item.path) {
    const { error: removeError } = await supabase.storage.from(item.bucket).remove([item.path]);
    if (removeError) {
      console.warn('Immagine rimossa dalla gallery, ma non dallo Storage:', removeError);
    }
  }

  await saveGallery(nextGallery);
}

function openMaps(): void {
  window.open(SITE.mapsUrl, '_blank');
}

function openWhatsapp(): void {
  window.open(SITE.whatsappUrl, '_blank');
}

function buildGalleryHtml(gallery: GalleryItem[], loading = false): string {
  const admin = isProbablyAdminFromStorage();

  if (loading) {
    return `
      <div class="im-gallery-native-placeholder">
        <div>
          <strong>Caricamento gallery...</strong>
          <span>Stiamo recuperando le immagini salvate.</span>
        </div>
      </div>
    `;
  }

  if (!gallery.length) {
    return `
      <div class="im-gallery-native-placeholder">
        <div>
          <strong>La gallery è pronta</strong>
          <span>${admin ? 'Carica le prime immagini dall’area admin.' : 'Le immagini saranno disponibili a breve.'}</span>
        </div>
      </div>
    `;
  }

  return `
    <div class="im-gallery-native-grid">
      ${gallery
        .map(
          (item, index) => `
            <div class="im-gallery-native-item">
              <img src="${escapeHtml(item.url)}" alt="${escapeHtml(item.title || 'Foto I.M. Distribuzione')}" loading="lazy">
              ${
                admin
                  ? `<button type="button" class="im-gallery-native-remove" data-im-remove-gallery="${index}">×</button>`
                  : ''
              }
            </div>
          `
        )
        .join('')}
    </div>
  `;
}

function buildReplacementSection(gallery: GalleryItem[] = [], loading = false): string {
  const admin = isProbablyAdminFromStorage();

  return `
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="text-center mb-16">
        <h2 class="text-4xl font-extrabold text-white mb-4 tracking-tight">Dove siamo</h2>
        <div class="w-24 h-1 bg-gradient-to-r from-[#2BB673] to-[#2BB673]/30 mx-auto rounded-full"></div>
        <p class="text-gray-400 mt-6 max-w-3xl mx-auto leading-relaxed">
          Passa a trovarci o contattaci direttamente per prodotti, disponibilità e forniture su misura.
        </p>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <div class="h-full bg-[hsl(220,18%,10%)] border border-[hsl(220,16%,18%)] rounded-lg transition-all duration-300 hover:border-[#2BB673]/30 hover:shadow-lg hover:shadow-[#2BB673]/5">
          <div class="p-6 pb-0">
            <h3 class="text-2xl font-extrabold text-white mb-2">${SITE.name}</h3>
            <p class="text-gray-400 leading-relaxed">Forniture professionali per attività, uffici, ristorazione e imprese.</p>
          </div>

          <div class="p-6 space-y-4">
            <div class="flex items-start gap-3 p-4 rounded-lg border border-[hsl(220,16%,18%)] bg-[hsl(220,18%,8%)]">
              <div class="text-[#87C38F] font-bold shrink-0">⌂</div>
              <div>
                <div class="font-bold text-white">${SITE.address}</div>
                <div class="text-gray-400">${SITE.city}</div>
              </div>
            </div>

            <div class="flex items-start gap-3 p-4 rounded-lg border border-[hsl(220,16%,18%)] bg-[hsl(220,18%,8%)]">
              <div class="text-[#87C38F] font-bold shrink-0">☎</div>
              <div>
                <div class="font-bold text-white">Contatto diretto</div>
                <a href="tel:${SITE.phone}" class="text-gray-400 hover:text-[#87C38F] transition-colors">${SITE.phoneLabel}</a>
              </div>
            </div>

            <div class="flex items-start gap-3 p-4 rounded-lg border border-[hsl(220,16%,18%)] bg-[hsl(220,18%,8%)]">
              <div class="text-[#87C38F] font-bold shrink-0">◎</div>
              <div>
                <div class="font-bold text-white">Area servita</div>
                <div class="text-gray-400">Labico, Roma e attività professionali del territorio</div>
              </div>
            </div>

            <div class="flex flex-col sm:flex-row gap-3 pt-2">
              <button type="button" data-im-maps class="inline-flex items-center justify-center rounded-lg px-5 py-3 bg-[#2BB673] hover:bg-[#1f9a5e] text-white font-bold transition-colors">Apri su Google Maps</button>
              <button type="button" data-im-whatsapp class="inline-flex items-center justify-center rounded-lg px-5 py-3 border border-[#2BB673]/30 text-[#87C38F] hover:bg-[#2BB673]/10 font-bold transition-colors">WhatsApp</button>
            </div>
          </div>
        </div>

        <div class="h-full bg-[hsl(220,18%,10%)] border border-[hsl(220,16%,18%)] rounded-lg overflow-hidden transition-all duration-300 hover:border-[#2BB673]/30 hover:shadow-lg hover:shadow-[#2BB673]/5">
          <div class="im-map-native">
            <div class="im-map-native-card">
              <div class="im-map-native-pin">⌖</div>
              <div class="font-extrabold text-white text-lg">Labico RM</div>
              <div class="text-gray-400 text-sm">${SITE.address}</div>
            </div>
          </div>
          <button type="button" data-im-maps class="w-full p-4 text-[#87C38F] font-bold bg-[hsl(220,18%,8%)] border-t border-[hsl(220,16%,18%)] hover:bg-[#2BB673]/10 hover:text-white transition-colors">Visualizza il percorso</button>
        </div>
      </div>

      <div class="bg-[hsl(220,18%,10%)] border border-[hsl(220,16%,18%)] rounded-lg overflow-hidden">
        <div class="p-6 im-gallery-native-toolbar">
          <div>
            <h3 class="text-2xl md:text-3xl font-extrabold text-white">Gallery</h3>
            <p class="text-gray-400 mt-2">Uno spazio unico per foto della sede, prodotti, forniture e attività quotidiana.</p>
            <p class="im-gallery-native-status mt-2">${gallery.length ? `${gallery.length} immagini caricate` : 'Nessuna immagine caricata'}</p>
          </div>

          ${
            admin
              ? `
                <div>
                  <input id="im-company-gallery-upload" type="file" accept="image/*" multiple hidden>
                  <label for="im-company-gallery-upload" class="inline-flex items-center justify-center rounded-lg px-5 py-3 bg-[#2BB673] hover:bg-[#1f9a5e] text-white font-bold transition-colors cursor-pointer">Aggiungi foto</label>
                </div>
              `
              : ''
          }
        </div>

        <div class="px-6 pb-6" data-im-gallery-content>${buildGalleryHtml(gallery, loading)}</div>
      </div>
    </div>
  `;
}

function replaceFakeMapWithGoogleMap(): void {
  const mapBox = document.querySelector<HTMLElement>('.im-map-native');
  if (!mapBox || mapBox.dataset.realMap === '1') return;

  mapBox.dataset.realMap = '1';
  mapBox.innerHTML = `
    <iframe
      title="I.M. Distribuzione - Via Agrolatino 24, Labico RM"
      src="${SITE.mapsEmbedUrl}"
      width="100%"
      height="100%"
      style="border:0; min-height:330px; display:block;"
      loading="lazy"
      referrerpolicy="no-referrer-when-downgrade">
    </iframe>
  `;
}

function attachEvents(section: HTMLElement): void {
  section.querySelectorAll('[data-im-maps]').forEach((button) => button.addEventListener('click', openMaps));
  section.querySelectorAll('[data-im-whatsapp]').forEach((button) => button.addEventListener('click', openWhatsapp));

  const input = section.querySelector<HTMLInputElement>('#im-company-gallery-upload');

  if (input) {
    input.addEventListener('change', async (event) => {
      try {
        const target = event.target as HTMLInputElement;
        const files = target.files;

        section.innerHTML = buildReplacementSection(await loadGallery(), true);
        attachEvents(section);
        replaceFakeMapWithGoogleMap();

        await uploadGalleryFiles(files);

        const gallery = await loadGallery();
        section.innerHTML = buildReplacementSection(gallery, false);
        attachEvents(section);
        replaceFakeMapWithGoogleMap();
      } catch (error) {
        console.error('Errore upload gallery:', error);
        alert('Errore durante il salvataggio della gallery.');
      }
    });
  }

  section.querySelectorAll<HTMLButtonElement>('[data-im-remove-gallery]').forEach((button) => {
    button.addEventListener('click', async () => {
      const index = Number(button.getAttribute('data-im-remove-gallery'));
      if (!confirm('Vuoi rimuovere questa immagine dalla gallery?')) return;

      try {
        await removeGalleryImage(index);
        const gallery = await loadGallery();
        section.innerHTML = buildReplacementSection(gallery, false);
        attachEvents(section);
        replaceFakeMapWithGoogleMap();
      } catch (error) {
        console.error('Errore rimozione immagine:', error);
        alert('Errore durante la rimozione dell’immagine.');
      }
    });
  });
}

async function patchServicesSection(force = false): Promise<void> {
  const heading = Array.from(document.querySelectorAll<HTMLHeadingElement>('h2')).find((h2) => {
    const text = (h2.textContent || '').trim().toLowerCase();
    return text === 'i nostri servizi' || text === 'dove siamo';
  });

  if (!heading) return;

  const section = heading.closest<HTMLElement>('section');
  if (!section) return;

  if (section.dataset.imDoveSiamoPatched === '1' && !force) {
    replaceFakeMapWithGoogleMap();
    return;
  }

  section.dataset.imDoveSiamoPatched = '1';
  section.className = 'py-24 bg-background';
  section.classList.remove('services-section');
  section.removeAttribute('style');

  section.innerHTML = buildReplacementSection([], true);
  attachEvents(section);
  replaceFakeMapWithGoogleMap();

  const gallery = await loadGallery();
  section.innerHTML = buildReplacementSection(gallery, false);
  attachEvents(section);
  replaceFakeMapWithGoogleMap();
}

export function initCompanyLocationGalleryPatch(): void {
  onReady(() => {
    void patchServicesSection();

    const observer = new MutationObserver(() => {
      void patchServicesSection();
      replaceFakeMapWithGoogleMap();
    });

    observer.observe(document.body, { childList: true, subtree: true });

    window.setTimeout(() => void patchServicesSection(), 500);
    window.setTimeout(() => void patchServicesSection(), 1500);
    window.setTimeout(() => void patchServicesSection(), 3000);
  });
}
